import { colors } from '@creditornot/cb-ingredients';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { DeliveryInsight } from 'apis/merchant-gateway/insights';
import { HorizontalBarChart, LegendChartType } from 'modules/experience-core';

import { getInsightsOrderTotalsByDeliveryType } from './getInsightsOrderTotalsByDeliveryType';
import { InsightResponse } from '../../types';
import { SummaryCard } from '../InsightSummary/SummaryTable';

type DeliveryTypesChartProps = {
  deliveryInsight: InsightResponse<DeliveryInsight>;
};

const CardContent = styled.div`
  padding: 20px 0;
`;

export const DeliveryTypesChartContent = ({
  performanceInsightsTotals,
}: {
  performanceInsightsTotals: {
    eatin: { regular: number; preorder: number };
    homedelivery: { regular: number; preorder: number };
    takeaway: { regular: number; preorder: number };
  };
}) => {
  const { formatMessage } = useIntl();

  const { eatin } = performanceInsightsTotals;
  const isLegacyEatinAvailable = eatin.regular || eatin.preorder;

  const deliveryTypes = [
    { key: 'homedelivery' as const, name: 'purchases.delivery-types.homedelivery' },
    { key: 'takeaway' as const, name: 'purchases.delivery-types.takeaway' },
    ...(isLegacyEatinAvailable ? [{ key: 'eatin' as const, name: 'purchases.delivery-types.eatin' }] : []),
  ];

  const chartData = deliveryTypes.map(({ key, name: id }) => ({
    name: formatMessage({ id }),
    regular: performanceInsightsTotals[key].regular,
    preorder: performanceInsightsTotals[key].preorder,
  }));

  const horizontalBarChart = (
    <HorizontalBarChart
      legends={{
        regular: {
          value: formatMessage({ id: 'insights.performance.regular-orders' }),
          color: colors.wolt88,
          type: LegendChartType.BAR,
        },
        preorder: {
          value: formatMessage({ id: 'insights.performance.preorders' }),
          color: colors.wolt32,
          type: LegendChartType.BAR,
        },
      }}
      data={chartData}
      summaryKey="insights.performance.total-orders"
    />
  );
  return horizontalBarChart;
};

export const DeliveryTypesChart = ({ deliveryInsight }: DeliveryTypesChartProps) => {
  const performanceInsightsTotals = getInsightsOrderTotalsByDeliveryType(deliveryInsight.data);
  const { formatMessage } = useIntl();

  return (
    <SummaryCard
      isFetching={deliveryInsight.isFetching}
      isError={deliveryInsight.isError || (deliveryInsight.isLoading && !deliveryInsight.isFetching)}
      onRetry={deliveryInsight.refetch}
      title={formatMessage({ id: 'insights.performance.order-by-delivery-type' })}
      tooltip={formatMessage({ id: 'insights.sales.order-types-description' })}
    >
      <CardContent>
        <DeliveryTypesChartContent performanceInsightsTotals={performanceInsightsTotals} />
      </CardContent>
    </SummaryCard>
  );
};
